import { useState, useEffect, FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  Box, Text, Button, Container, SimpleGrid, TextInput, Textarea, Stack,
  Group, Alert, ThemeIcon, SegmentedControl, Badge,
} from '@mantine/core'
import {
  IconQrcode, IconArrowsExchange, IconTool, IconFileCertificate,
  IconSignature, IconClipboardCheck, IconChartBar, IconDeviceMobile,
  IconCheck, IconArrowRight, IconShieldCheck, IconHeadset, IconAdjustments,
  IconSparkles, IconCloud, IconDevices, IconPlayerPlayFilled,
} from '@tabler/icons-react'
import axios from 'axios'
import { API_BASE_URL } from '../api/client'

// ── นับสถิติเข้าชม/กดลิงก์แบบเบาที่สุด — ไม่รอผลลัพธ์ ไม่มีทาง throw จนกระทบ UX ของเว็บขาย
// เก็บแค่ "จำนวน" ไม่มี IP/identity ใดๆ ติดไปด้วย (ดู platform-analytics.service.ts ฝั่ง backend) ──
function trackEvent(eventType: 'pageview' | 'click', label?: string) {
  axios.post(`${API_BASE_URL}/public/track`, { eventType, path: '/', label }).catch(() => {})
}

// ── สีแบรนด์ใหม่ตามโลโก้ AssetOS (ไล่สีน้ำเงิน→เขียวมิ้นท์ บนพื้นเนวี่เข้ม) — sample มาจาก
// ไฟล์โลโก้จริง ไม่ใช่เดาเอง: NAVY จากพื้นหลังโลโก้, BLUE จากส่วนริบบิ้นหลัก, TEAL จากส่วนไล่สี
// เขียวมิ้นท์ที่ปลายริบบิ้น ──
const NAVY = '#0a0f1c'
const NAVY_SOFT = '#111a2e'
const BLUE = '#2563eb'
const TEAL = '#10b981'
const TEAL_LIGHT = '#14b8a6'

const FEATURE_ICONS = [IconQrcode, IconArrowsExchange, IconTool, IconFileCertificate, IconSignature, IconClipboardCheck, IconChartBar, IconDeviceMobile]
const WHYUS_ICONS = [IconShieldCheck, IconHeadset, IconAdjustments]

// ── ค่าเริ่มต้น "เผื่อไว้" ตรงกับ default ฝั่ง backend (platform-pricing.service.ts) ทุกตัวเป๊ะ —
// ใช้โชว์ระหว่างรอ fetch หรือถ้า /public/pricing ล้ม ไม่ให้หน้าราคาว่างเปล่า/พังไปเลย ──
interface PublicPricing {
  monthlyPrice: number
  annualOriginalPrice: number
  annualPrice: number
  currency: string
  features: string[]
}
const DEFAULT_PRICING: PublicPricing = {
  monthlyPrice: 299,
  annualOriginalPrice: 3500,
  annualPrice: 1990,
  currency: 'THB',
  features: [
    'อัปเดตเวอร์ชันใหม่ล่าสุดตลอดเวลา ไม่มีค่าใช้จ่ายเพิ่ม',
    'ใช้งานได้ทั้งคอมพิวเตอร์และมือถือ ไม่จำกัดอุปกรณ์',
    'ข้อมูลสำรองอัตโนมัติ ปลอดภัยด้วยการเข้ารหัส',
    'ทีมงานพร้อมซัพพอร์ตเมื่อมีปัญหาการใช้งาน',
  ],
}

// ── ตั้ง SEO meta tags + JSON-LD แบบ runtime เพราะหน้านี้เป็น SPA (index.html เดียวใช้ร่วมกับ
// ทุกหน้าในแอป) — ไม่เพิ่ม dependency ใหม่ (react-helmet ฯลฯ) จัดการ DOM ตรงๆ ตอน mount แล้ว
// ปล่อยทิ้งไว้ตอน unmount ก็ไม่เป็นไร เพราะหน้านี้คือหน้าแรกที่คนเข้าเว็บเสมอ (root route) ──
function useMarketingSeo(title: string, description: string) {
  useEffect(() => {
    const prevTitle = document.title
    document.title = title

    const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`)
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }
      el.setAttribute('content', content)
    }

    setMeta('name', 'description', description)
    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:type', 'website')
    setMeta('name', 'twitter:card', 'summary_large_image')
    setMeta('name', 'twitter:title', title)
    setMeta('name', 'twitter:description', description)

    const ldJson = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'AssetOS',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web, iOS, Android',
      description,
      offers: {
        '@type': 'Offer',
        price: String(DEFAULT_PRICING.annualPrice),
        priceCurrency: DEFAULT_PRICING.currency,
      },
    }
    let script = document.getElementById('marketing-ld-json') as HTMLScriptElement | null
    if (!script) {
      script = document.createElement('script')
      script.id = 'marketing-ld-json'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(ldJson)

    return () => { document.title = prevTitle }
  }, [title, description])
}

export default function MarketingPage() {
  const { t, i18n } = useTranslation()
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', message: '' })
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual')
  const [pricing, setPricing] = useState<PublicPricing>(DEFAULT_PRICING)

  const featureKeys = ['tracking', 'borrow', 'maintenance', 'licenses', 'handover', 'audit', 'reports', 'mobile']
  const whyUsKeys = ['security', 'support', 'flexible']
  const stepKeys = ['1', '2', '3', '4']

  useMarketingSeo(t('marketing.seo.title'), t('marketing.seo.description'))

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setStatus('sending')
    try {
      await axios.post(`${API_BASE_URL}/public/contact`, form)
      setStatus('success')
      setForm({ name: '', company: '', email: '', phone: '', message: '' })
    } catch {
      setStatus('error')
    }
  }

  // ── นับ pageview ครั้งเดียวตอนเปิดหน้า — ไม่ผูกกับ dependency ไหนเลยเพราะหน้านี้มีแค่หน้าเดียว
  // ไม่มี route ย่อยให้ต้องแยกนับ ──
  useEffect(() => { trackEvent('pageview') }, [])

  // ── ราคาจริงดึงจาก Platform Console (Settings > Pricing) — ล้มเหลวเงียบๆ แล้วใช้ DEFAULT_PRICING
  // ต่อไป ไม่ให้หน้าเว็บขายพังเพราะ endpoint นี้ล่ม ──
  useEffect(() => {
    axios.get<PublicPricing>(`${API_BASE_URL}/public/pricing`)
      .then(({ data }) => setPricing(data))
      .catch(() => {})
  }, [])

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  const scrollToWithTracking = (id: string, label: string) => {
    trackEvent('click', label)
    scrollTo(id)
  }

  const savePct = pricing.annualOriginalPrice > 0
    ? Math.round((1 - pricing.annualPrice / pricing.annualOriginalPrice) * 100)
    : 0

  return (
    <Box style={{ fontFamily: 'Inter, sans-serif', color: '#1a2333', background: '#fff' }}>
      {/* ── Nav ── */}
      <Box style={{ borderBottom: '1px solid #e8ecf1', position: 'sticky', top: 0, background: '#fffdf9', zIndex: 10 }}>
        <Container size="lg">
          <Group justify="space-between" py={14}>
            <Group gap={8}>
              <img src="/logo-mark.png" alt="AssetOS" style={{ width: 30, height: 30, borderRadius: 8 }} />
              <Text style={{ fontWeight: 800, fontSize: 19, color: NAVY, letterSpacing: '-0.3px' }}>AssetOS</Text>
            </Group>
            <Group gap={28} visibleFrom="sm">
              <Text component="button" onClick={() => scrollTo('features')} style={navLinkStyle}>{t('marketing.nav.features')}</Text>
              <Text component="button" onClick={() => scrollTo('pricing')} style={navLinkStyle}>{t('marketing.pricing.title')}</Text>
              <Text component="button" onClick={() => scrollTo('how')} style={navLinkStyle}>{t('marketing.nav.howItWorks')}</Text>
              <Text component="button" onClick={() => scrollTo('why')} style={navLinkStyle}>{t('marketing.nav.whyUs')}</Text>
              <Text component="button" onClick={() => scrollTo('contact')} style={navLinkStyle}>{t('marketing.nav.contact')}</Text>
            </Group>
            <Group gap={10}>
              <Group gap={4}>
                {(['th', 'en'] as const).map(lang => (
                  <Text
                    key={lang} component="button" onClick={() => i18n.changeLanguage(lang)}
                    style={{
                      fontSize: 12, fontWeight: 700, padding: '4px 9px', borderRadius: 6, cursor: 'pointer',
                      background: i18n.language === lang ? NAVY : 'transparent',
                      color: i18n.language === lang ? '#fff' : '#8a93a3',
                    }}
                  >
                    {lang.toUpperCase()}
                  </Text>
                ))}
              </Group>
              <Button component={Link} to="/login" variant="subtle" color="dark" size="xs">
                {t('marketing.nav.login')}
              </Button>
              <Button onClick={() => scrollToWithTracking('contact', 'cta_request_demo_nav')} size="xs" styles={{ root: { background: `linear-gradient(135deg, ${BLUE}, ${TEAL})` } }}>
                {t('marketing.nav.requestDemo')}
              </Button>
            </Group>
          </Group>
        </Container>
      </Box>

      {/* ── Hero — พื้นเข้ม + grid pattern + glow blur ให้ดูเป็นเว็บ Tech มากขึ้น ── */}
      <Box style={{ position: 'relative', background: NAVY, overflow: 'hidden' }}>
        {/* grid overlay */}
        <Box style={{
          position: 'absolute', inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 0%, #000 50%, transparent 100%)',
        }} />
        {/* glow blobs */}
        <Box style={{ position: 'absolute', top: -120, left: '8%', width: 420, height: 420, borderRadius: '50%', background: BLUE, opacity: 0.22, filter: 'blur(110px)' }} />
        <Box style={{ position: 'absolute', top: -60, right: '6%', width: 380, height: 380, borderRadius: '50%', background: TEAL, opacity: 0.2, filter: 'blur(110px)' }} />

        <Container size="lg" py={{ base: 64, sm: 96 }} style={{ position: 'relative' }}>
          <Box style={{ maxWidth: 680 }}>
            <Group gap={8} mb={18}>
              <Box style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999,
                border: '1px solid rgba(20,184,166,0.35)', background: 'rgba(20,184,166,0.08)',
              }}>
                <IconSparkles size={13} color={TEAL_LIGHT} />
                <Text style={{ color: TEAL_LIGHT, fontWeight: 700, fontSize: 12.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {t('marketing.hero.eyebrow')}
                </Text>
              </Box>
            </Group>
            <Text component="h1" style={{ fontSize: 48, fontWeight: 800, lineHeight: 1.12, color: '#fff', letterSpacing: '-1.2px', margin: 0 }}>
              {t('marketing.hero.headline')}
            </Text>
            <Text style={{ fontSize: 17, color: '#a8b3c4', marginTop: 20, lineHeight: 1.65, maxWidth: 560 }}>
              {t('marketing.hero.subheadline')}
            </Text>
            <Group mt={30} gap={12}>
              <Button size="md" rightSection={<IconArrowRight size={16} />} onClick={() => scrollToWithTracking('contact', 'cta_request_demo_hero')}
                styles={{ root: { background: `linear-gradient(135deg, ${BLUE}, ${TEAL})`, fontWeight: 700 } }}>
                {t('marketing.hero.ctaPrimary')}
              </Button>
              <Button size="md" variant="default" onClick={() => scrollTo('pricing')}
                styles={{ root: { background: 'transparent', borderColor: 'rgba(255,255,255,0.25)', color: '#fff' } }}>
                {t('marketing.hero.ctaSecondary')}
              </Button>
            </Group>

            <Group gap={10} mt={28} wrap="wrap">
              {[
                { icon: IconCloud, label: 'Cloud-based' },
                { icon: IconDevices, label: 'Web · Desktop · Mobile' },
                { icon: IconShieldCheck, label: 'Encrypted & multi-tenant' },
              ].map(({ icon: Icon, label }) => (
                <Group key={label} gap={6} style={{
                  padding: '6px 12px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)',
                }}>
                  <Icon size={14} color="#a8b3c4" />
                  <Text style={{ fontSize: 12.5, color: '#c3cbd8', fontWeight: 600 }}>{label}</Text>
                </Group>
              ))}
            </Group>

            <Text style={{ fontSize: 13, color: '#6b7891', marginTop: 20 }}>{t('marketing.hero.trustline')}</Text>
          </Box>
        </Container>
      </Box>

      {/* ── Problem ── */}
      <Container size="lg" py={50}>
        <Box style={{ background: '#fdf6ec', borderRadius: 16, padding: '32px 36px', borderLeft: `4px solid #e8a33d` }}>
          <Text style={{ fontSize: 20, fontWeight: 700, color: NAVY, marginBottom: 8 }}>{t('marketing.problem.title')}</Text>
          <Text style={{ fontSize: 15, color: '#5a6478', lineHeight: 1.7 }}>{t('marketing.problem.body')}</Text>
        </Box>
      </Container>

      {/* ── Features ── */}
      <Box id="features" style={{ background: '#f8faf9' }}>
        <Container size="lg" py={70}>
          <Box style={{ textAlign: 'center', maxWidth: 560, margin: '0 auto 44px' }}>
            <Text component="h2" style={{ fontSize: 30, fontWeight: 800, color: NAVY, letterSpacing: '-0.5px' }}>
              {t('marketing.features.title')}
            </Text>
            <Text style={{ fontSize: 15, color: '#6b7480', marginTop: 8 }}>{t('marketing.features.subtitle')}</Text>
          </Box>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing={20}>
            {featureKeys.map((key, i) => {
              const Icon = FEATURE_ICONS[i]
              return (
                <Box key={key} className="assetos-feature-card" style={{ background: '#fff', border: '1px solid #e8ecf1', borderRadius: 14, padding: 22, transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}>
                  <ThemeIcon size={40} radius={10} style={{ background: `linear-gradient(135deg, ${BLUE}1a, ${TEAL}26)` }}>
                    <Icon size={21} color={TEAL} />
                  </ThemeIcon>
                  <Text style={{ fontWeight: 700, fontSize: 15, color: NAVY, marginTop: 14 }}>
                    {t(`marketing.features.items.${key}.title`)}
                  </Text>
                  <Text style={{ fontSize: 13.5, color: '#6b7480', marginTop: 6, lineHeight: 1.6 }}>
                    {t(`marketing.features.items.${key}.desc`)}
                  </Text>
                </Box>
              )
            })}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Video — placeholder รอใส่วิดีโอสาธิตจริง (ผู้ใช้จะตัดต่อแล้วใส่ภายหลัง) ── */}
      <Container size="lg" py={70}>
        <Box style={{ textAlign: 'center', maxWidth: 560, margin: '0 auto 36px' }}>
          <Text style={{ color: TEAL, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>
            {t('marketing.video.eyebrow')}
          </Text>
          <Text component="h2" style={{ fontSize: 30, fontWeight: 800, color: NAVY, letterSpacing: '-0.5px' }}>
            {t('marketing.video.title')}
          </Text>
          <Text style={{ fontSize: 15, color: '#6b7480', marginTop: 8 }}>{t('marketing.video.subtitle')}</Text>
        </Box>

        {/* ── TODO (ผู้ใช้): แทนที่ Box ด้านล่างทั้งก้อนด้วย <video> หรือ <iframe> จริงตอนตัดต่อเสร็จ ── */}
        <Box style={{
          position: 'relative', maxWidth: 860, margin: '0 auto', aspectRatio: '16/9', borderRadius: 20, overflow: 'hidden',
          background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_SOFT} 60%, #152037 100%)`,
          border: '1px solid #232c44', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Box style={{
            position: 'absolute', inset: 0,
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)',
            backgroundSize: '36px 36px',
          }} />
          <Stack align="center" gap={10} style={{ position: 'relative' }}>
            <ThemeIcon size={64} radius={999} style={{ background: `linear-gradient(135deg, ${BLUE}, ${TEAL})`, boxShadow: '0 0 0 10px rgba(255,255,255,0.06)' }}>
              <IconPlayerPlayFilled size={26} color="#fff" />
            </ThemeIcon>
            <Text style={{ color: '#a8b3c4', fontSize: 13.5, fontWeight: 600 }}>{t('marketing.video.comingSoon')}</Text>
          </Stack>
        </Box>
      </Container>

      {/* ── Pricing ── */}
      <Box id="pricing" style={{ background: '#f8faf9' }}>
        <Container size="sm" py={70}>
          <Box style={{ textAlign: 'center', marginBottom: 40 }}>
            <Text style={{ color: TEAL, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>
              {t('marketing.pricing.eyebrow')}
            </Text>
            <Text component="h2" style={{ fontSize: 30, fontWeight: 800, color: NAVY, letterSpacing: '-0.5px' }}>
              {t('marketing.pricing.title')}
            </Text>
            <Text style={{ fontSize: 15, color: '#6b7480', marginTop: 8 }}>{t('marketing.pricing.subtitle')}</Text>
          </Box>

          <Group justify="center" mb={28}>
            <SegmentedControl
              value={billingCycle}
              onChange={(v) => setBillingCycle(v as 'monthly' | 'annual')}
              data={[
                { label: t('marketing.pricing.monthly'), value: 'monthly' },
                { label: t('marketing.pricing.annual'), value: 'annual' },
              ]}
              color="dark"
              radius={999}
              size="md"
            />
          </Group>

          <Box style={{
            maxWidth: 420, margin: '0 auto', borderRadius: 20, padding: 2,
            background: `linear-gradient(135deg, ${BLUE}, ${TEAL})`,
          }}>
            <Box style={{ background: '#fff', borderRadius: 18, padding: '36px 32px' }}>
              <Group justify="center" gap={10} mb={6}>
                {billingCycle === 'annual' && savePct > 0 && (
                  <Badge variant="light" color="teal" radius="sm" size="lg">
                    {t('marketing.pricing.savePct', { pct: savePct })}
                  </Badge>
                )}
              </Group>

              <Group justify="center" align="flex-end" gap={6}>
                {billingCycle === 'annual' && pricing.annualOriginalPrice > pricing.annualPrice && (
                  <Text style={{ fontSize: 20, color: '#aab2c0', textDecoration: 'line-through', marginBottom: 6 }}>
                    {pricing.annualOriginalPrice.toLocaleString()}
                  </Text>
                )}
                <Text style={{ fontSize: 52, fontWeight: 800, color: NAVY, letterSpacing: '-1.5px', lineHeight: 1 }}>
                  {(billingCycle === 'monthly' ? pricing.monthlyPrice : pricing.annualPrice).toLocaleString()}
                </Text>
                <Text style={{ fontSize: 16, color: '#6b7480', fontWeight: 600, marginBottom: 6 }}>
                  {pricing.currency} {billingCycle === 'monthly' ? t('marketing.pricing.perMonth') : t('marketing.pricing.perYear')}
                </Text>
              </Group>
              {billingCycle === 'annual' && (
                <Text style={{ textAlign: 'center', fontSize: 12.5, color: '#8a93a3', marginTop: 4 }}>
                  {t('marketing.pricing.billedAnnually', { price: pricing.annualPrice.toLocaleString() })}
                </Text>
              )}

              <Stack gap={10} mt={26}>
                {pricing.features.map((feature) => (
                  <Group key={feature} gap={10} wrap="nowrap" align="flex-start">
                    <ThemeIcon size={20} radius={999} color="teal" variant="light" mt={1}>
                      <IconCheck size={13} />
                    </ThemeIcon>
                    <Text style={{ fontSize: 14, color: '#3d4759', lineHeight: 1.5 }}>{feature}</Text>
                  </Group>
                ))}
              </Stack>

              <Button
                fullWidth size="md" mt={28} rightSection={<IconArrowRight size={16} />}
                onClick={() => scrollToWithTracking('contact', 'cta_pricing')}
                styles={{ root: { background: `linear-gradient(135deg, ${BLUE}, ${TEAL})`, fontWeight: 700 } }}
              >
                {t('marketing.pricing.cta')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── How it works ── */}
      <Container id="how" size="lg" py={70}>
        <Text component="h2" style={{ fontSize: 30, fontWeight: 800, color: NAVY, textAlign: 'center', letterSpacing: '-0.5px', marginBottom: 44 }}>
          {t('marketing.howItWorks.title')}
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing={28}>
          {stepKeys.map((key, i) => (
            <Box key={key} style={{ position: 'relative' }}>
              <Text style={{
                fontSize: 38, fontWeight: 800,
                background: `linear-gradient(135deg, ${BLUE}33, ${TEAL}33)`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                {String(i + 1).padStart(2, '0')}
              </Text>
              <Text style={{ fontWeight: 700, fontSize: 15, color: NAVY, marginTop: 4 }}>
                {t(`marketing.howItWorks.steps.${key}.title`)}
              </Text>
              <Text style={{ fontSize: 13.5, color: '#6b7480', marginTop: 6, lineHeight: 1.6 }}>
                {t(`marketing.howItWorks.steps.${key}.desc`)}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* ── Why us ── */}
      <Box id="why" style={{ background: NAVY, position: 'relative', overflow: 'hidden' }}>
        <Box style={{ position: 'absolute', bottom: -140, left: '50%', transform: 'translateX(-50%)', width: 500, height: 500, borderRadius: '50%', background: TEAL, opacity: 0.12, filter: 'blur(120px)' }} />
        <Container size="lg" py={70} style={{ position: 'relative' }}>
          <Text component="h2" style={{ fontSize: 30, fontWeight: 800, color: '#fff', textAlign: 'center', letterSpacing: '-0.5px', marginBottom: 44 }}>
            {t('marketing.whyUs.title')}
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={28}>
            {whyUsKeys.map((key, i) => {
              const Icon = WHYUS_ICONS[i]
              return (
                <Box key={key} style={{ textAlign: 'center' }}>
                  <ThemeIcon size={48} radius={12} style={{ background: 'rgba(20,184,166,0.15)', margin: '0 auto' }}>
                    <Icon size={24} color={TEAL_LIGHT} />
                  </ThemeIcon>
                  <Text style={{ fontWeight: 700, fontSize: 16, color: '#fff', marginTop: 16 }}>
                    {t(`marketing.whyUs.items.${key}.title`)}
                  </Text>
                  <Text style={{ fontSize: 13.5, color: '#a8b3c4', marginTop: 6, lineHeight: 1.6 }}>
                    {t(`marketing.whyUs.items.${key}.desc`)}
                  </Text>
                </Box>
              )
            })}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Contact ── */}
      <Container id="contact" size="sm" py={80}>
        <Box style={{ textAlign: 'center', marginBottom: 32 }}>
          <Text component="h2" style={{ fontSize: 28, fontWeight: 800, color: NAVY, letterSpacing: '-0.5px' }}>
            {t('marketing.contact.title')}
          </Text>
          <Text style={{ fontSize: 14.5, color: '#6b7480', marginTop: 8 }}>{t('marketing.contact.subtitle')}</Text>
        </Box>

        {status === 'success' ? (
          <Alert color="teal" variant="light" radius="md" icon={<IconCheck size={18} />}>
            {t('marketing.contact.form.success')}
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} style={{ background: '#f8faf9', border: '1px solid #e8ecf1', borderRadius: 16, padding: 28 }}>
            <Stack gap={14}>
              {status === 'error' && <Alert color="red" variant="light" radius="md">{t('marketing.contact.form.error')}</Alert>}
              <SimpleGrid cols={2} spacing={14}>
                <TextInput label={t('marketing.contact.form.name')} required value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
                <TextInput label={t('marketing.contact.form.company')} required value={form.company}
                  onChange={e => setForm(p => ({ ...p, company: e.target.value }))} />
              </SimpleGrid>
              <SimpleGrid cols={2} spacing={14}>
                <TextInput label={t('marketing.contact.form.email')} type="email" required value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
                <TextInput label={t('marketing.contact.form.phone')} value={form.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
              </SimpleGrid>
              <Textarea label={t('marketing.contact.form.message')} minRows={3} value={form.message}
                onChange={e => setForm(p => ({ ...p, message: e.target.value }))} />
              <Button type="submit" loading={status === 'sending'} size="md" mt={4}
                styles={{ root: { background: `linear-gradient(135deg, ${BLUE}, ${TEAL})`, fontWeight: 700 } }}>
                {status === 'sending' ? t('marketing.contact.form.submitting') : t('marketing.contact.form.submit')}
              </Button>
            </Stack>
          </Box>
        )}
      </Container>

      {/* ── Footer ── */}
      <Box style={{ borderTop: '1px solid #e8ecf1', background: '#fdfdfc' }}>
        <Container size="lg" py={28}>
          <Group justify="space-between" wrap="wrap">
            <Group gap={8}>
              <img src="/logo-mark.png" alt="AssetOS" style={{ width: 22, height: 22, borderRadius: 6 }} />
              <Box>
                <Text style={{ fontWeight: 800, fontSize: 15, color: NAVY }}>AssetOS</Text>
                <Text style={{ fontSize: 12.5, color: '#8a93a3', marginTop: 2 }}>{t('marketing.footer.tagline')}</Text>
              </Box>
            </Group>
            <Text style={{ fontSize: 13, color: '#6b7480' }}>
              {t('marketing.footer.existingCustomer')}{' '}
              <Link to="/login" style={{ color: TEAL, fontWeight: 600, textDecoration: 'none' }}>
                {t('marketing.footer.loginLink')}
              </Link>
            </Text>
            <Text style={{ fontSize: 12, color: '#a3acb8' }}>
              © {new Date().getFullYear()} AssetOS — {t('marketing.footer.rights')}
            </Text>
          </Group>
        </Container>
      </Box>
    </Box>
  )
}

const navLinkStyle = {
  fontSize: 14, fontWeight: 600, color: '#4a5568', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
}
